import { Component,  OnInit, OnDestroy, inject, WritableSignal, signal, Inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService, User } from './auth.service';
import { AppService } from './services/app.service';
import { SettingsDialogComponent } from './components/settings-dialog/settings-dialog';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  timestamp: Date;
  read: boolean;
}

interface PageInfo {
  title: string;
  breadcrumbs: string[];
  icon: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatBadgeModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
    MatDividerModule,
    TranslateModule
  ],
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss']
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  public app = inject(AppService)
  user: WritableSignal<User | undefined> = signal(undefined)

  currentUser: User | null = null;
  currentPage: PageInfo = { title: 'Panel Główny', breadcrumbs: ['Dashboard'], icon: 'dashboard' };
  notifications: Notification[] = [];
  unreadCount = 0;
  private destroy$ = new Subject<void>();

  private authService = inject(AuthService);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  // Mapowanie tras do informacji o stronach
  private pageMap: Record<string, PageInfo> = {
    '/dashboard': { title: 'Panel Główny', breadcrumbs: ['Dashboard'], icon: 'dashboard' },
    '/clients': { title: 'Klienci', breadcrumbs: ['Dashboard', 'Klienci'], icon: 'people' },
    '/appointments': { title: 'Wizyty', breadcrumbs: ['Dashboard', 'Wizyty'], icon: 'event' },
    '/consultations': { title: 'Konsultacje', breadcrumbs: ['Dashboard', 'Konsultacje'], icon: 'medical_services' },
    '/trichoscopy': { title: 'Trichoskopia', breadcrumbs: ['Dashboard', 'Trichoskopia'], icon: 'camera_alt' },
    '/reports': { title: 'Raporty', breadcrumbs: ['Dashboard', 'Raporty'], icon: 'assessment' },
    'takss': { title: 'Mapa okolicy', breadcrumbs: ['Dashboard', 'Admin', 'Mapa okolicy'], icon: 'map' }
  };

  ngOnInit() {
    this.authService.currentUser$.subscribe(user => {
      console.log(user)
      this.currentUser = user;
      if (user !== undefined && user !== null) {
        this.user.set(user);
      }
    });

    // Subskrypcja zmian routingu
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateCurrentPage(event.url);
      }
    });

    // Inicjalizacja powiadomień
    this.initializeNotifications();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateCurrentPage(url: string) {
    const path = url;
    this.currentPage = this.pageMap[path] || this.pageMap['/dashboard'];
  }

  private initializeNotifications() {
    // Symulacja powiadomień
    this.notifications = [
      {
        id: '1',
        title: 'Nowa wizyta',
        message: 'Dr. Smith dodał nową wizytę na jutro',
        type: 'info',
        timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30 min temu
        read: false
      },
      {
        id: '2',
        title: 'Wyniki trichoskopii',
        message: 'Wyniki badania klienta #123 są gotowe',
        type: 'success',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 godziny temu
        read: false
      },
      {
        id: '3',
        title: 'Przypomnienie',
        message: 'Konsultacja z klientem #456 za godzinę',
        type: 'warning',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4), // 4 godziny temu
        read: true
      }
    ];
    this.updateUnreadCount();
  }

  private updateUnreadCount() {
    this.unreadCount = this.notifications.filter(n => !n.read).length;
  }

  markNotificationAsRead(notificationId: string) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      this.updateUnreadCount();
    }
  }

  markAllAsRead() {
    this.notifications.forEach(n => n.read = true);
    this.updateUnreadCount();
  }

  getNotificationIcon(type: string): string {
    switch (type) {
      case 'info': return 'info';
      case 'warning': return 'warning';
      case 'error': return 'error';
      case 'success': return 'check_circle';
      default: return 'notifications';
    }
  }

  getNotificationColor(type: string): string {
    switch (type) {
      case 'info': return 'primary';
      case 'warning': return 'accent';
      case 'error': return 'warn';
      case 'success': return 'primary';
      default: return 'primary';
    }
  }

  formatTimestamp(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes} min temu`;
    if (hours < 24) return `${hours} godz. temu`;
    if (days < 7) return `${days} dni temu`;
    return timestamp.toLocaleDateString('pl-PL');
  }

  openSettings(): void {
    this.dialog.open(SettingsDialogComponent);
  }

  logout() {
    this.authService.logout();
  }

  toggleSidenav() {
    this.app.toggleSidenav();
  }
} 